import { HolidayRuleInterface, OpenStatusResult, ResolvedSchedule, ScheduleExceptionInterface, ScheduleInterface } from "@swivl-mono/types";

import { APIService } from "../Service/API.Service";

// LINK apps/server/src/API/Schedule/Schedule.Controller.ts
export class ScheduleAPI {
  // ─── Read / compute ───────────────────────────────────────────────────────

  static async resolve(orgId: number, locationId: number, slug: string): Promise<ResolvedSchedule | null> {
    return APIService.Server.get<ResolvedSchedule | null>(`/schedule/org/${orgId}/location/${locationId}/resolve`, { slug });
  }

  static async getOpenStatus(orgId: number, locationId: number, slug: string, at?: string): Promise<OpenStatusResult> {
    return APIService.Server.get<OpenStatusResult>(`/schedule/org/${orgId}/location/${locationId}/open-status`, { slug, at });
  }

  static async getOpenStatusBulk(orgId: number, locationIds: number[], slug: string, at?: string): Promise<Record<number, OpenStatusResult>> {
    return APIService.Server.post<Record<number, OpenStatusResult>>(`/schedule/org/${orgId}/open-status-bulk`, { locationIds, slug, at });
  }

  // ─── Schedule CRUD ────────────────────────────────────────────────────────

  static async listAllSchedulesForOrg(orgId: number): Promise<ScheduleInterface[]> {
    return APIService.Server.get<ScheduleInterface[]>(`/schedule/org/${orgId}/schedules`);
  }

  static async listSchedulesAtScope(orgId: number, scope: { groupId?: number; locationId?: number }): Promise<ScheduleInterface[]> {
    return APIService.Server.get<ScheduleInterface[]>(`/schedule/org/${orgId}/schedules-at-scope`, scope);
  }

  static async upsertSchedule(orgId: number, schedule: Partial<ScheduleInterface>): Promise<ScheduleInterface> {
    return APIService.Server.post<ScheduleInterface>(`/schedule/org/${orgId}/schedule`, schedule);
  }

  static async deleteSchedule(orgId: number, scheduleId: number): Promise<{ ok: true }> {
    return APIService.Server.delete(`/schedule/org/${orgId}/schedule/${scheduleId}`);
  }

  /** Remove a custom schedule group everywhere in the org (its hours at every scope and its date overrides). */
  static async deleteScheduleGroup(orgId: number, slug: string): Promise<{ ok: true }> {
    return APIService.Server.delete(`/schedule/org/${orgId}/schedule-group/${encodeURIComponent(slug)}`);
  }

  // ─── Holiday rules ────────────────────────────────────────────────────────

  static async listApplicableHolidayRulesForLocation(orgId: number, locationId: number): Promise<HolidayRuleInterface[]> {
    return APIService.Server.get<HolidayRuleInterface[]>(`/schedule/org/${orgId}/location/${locationId}/holiday-rules`);
  }

  static async listHolidayRulesAtScope(orgId: number, scope: { groupId?: number; locationId?: number }): Promise<HolidayRuleInterface[]> {
    return APIService.Server.get<HolidayRuleInterface[]>(`/schedule/org/${orgId}/holiday-rules-at-scope`, scope);
  }

  static async upsertHolidayRule(orgId: number, rule: Partial<HolidayRuleInterface>): Promise<HolidayRuleInterface> {
    return APIService.Server.post<HolidayRuleInterface>(`/schedule/org/${orgId}/holiday-rule`, rule);
  }

  static async deleteHolidayRule(orgId: number, ruleId: number): Promise<{ ok: true }> {
    return APIService.Server.delete(`/schedule/org/${orgId}/holiday-rule/${ruleId}`);
  }

  // ─── Exceptions ───────────────────────────────────────────────────────────

  /**
   * List exceptions at a single (scope, slug). The PM UI calls this per
   * scope+slug pair it renders.
   */
  static async listExceptionsAtScope(
    orgId: number,
    scope: { groupId?: number; locationId?: number },
    slug: string
  ): Promise<ScheduleExceptionInterface[]> {
    return APIService.Server.get<ScheduleExceptionInterface[]>(`/schedule/org/${orgId}/schedule-exceptions-at-scope`, { ...scope, slug });
  }

  static async upsertException(orgId: number, exception: Partial<ScheduleExceptionInterface>): Promise<ScheduleExceptionInterface> {
    return APIService.Server.post<ScheduleExceptionInterface>(`/schedule/org/${orgId}/schedule-exception`, exception);
  }

  static async deleteException(orgId: number, exceptionId: number): Promise<{ ok: true }> {
    return APIService.Server.delete(`/schedule/org/${orgId}/schedule-exception/${exceptionId}`);
  }
}
